import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import {
  Badge,
  Button,
  Col,
  Collapse,
  Container,
  Form,
  Modal,
  Row,
  Table
} from "react-bootstrap"
import { toast } from "react-toastify"
import { roleCheck } from "utils/index"
import { ACCOUNTS, OPERATIONS } from "utils/constants"
import {
  approveLeaveApplication,
  declineLeaveApplication,
  getLeaveApplicationsDashboard,
  openLeaveApplicationsDashboardPdf,
  parseLeaveApplicationAttributes,
  removedFromProfileCellProps,
  updateLeaveApplicationCoverage
} from "requesters/leaveApplicationRequester"
import { currentWeekPeriod } from "utils/weekPeriod"
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../reducers"

const SYSTEM_ADMIN_ROLES = ["admin", "super_admin"]

function isSystemAdmin(systemRole) {
  return SYSTEM_ADMIN_ROLES.includes(systemRole)
}

function canAccessDashboard(systemRole, currentRole) {
  return isSystemAdmin(systemRole)
    || roleCheck(systemRole, currentRole, ACCOUNTS)
    || roleCheck(systemRole, currentRole, OPERATIONS)
}

function canEditCoverage(systemRole, currentRole) {
  return isSystemAdmin(systemRole) || roleCheck(systemRole, currentRole, OPERATIONS)
}

function isAccountsTeam(currentRole) {
  if (Array.isArray(currentRole)) return currentRole.some(role => role.includes(ACCOUNTS))
  return typeof currentRole === "string" ? currentRole.includes(ACCOUNTS) : false
}

function parseDashboardRows(payload) {
  if (!payload?.data) return []
  const list = Array.isArray(payload.data) ? payload.data : [payload.data]
  return list.map(row => {
    const a = row.attributes || {}
    return {
      id: row.id,
      submitterId: a.submitter_id ?? a.submitterId,
      submitterName: a.submitter_name || a.submitterName,
      submitterEmail: a.submitter_email || a.submitterEmail,
      firstname: a.firstname,
      surname: a.surname,
      position: a.position,
      phone: a.phone,
      comments: a.comments,
      lastDayOfWork: a.last_day_of_work || a.lastDayOfWork,
      returnToWorkDate: a.return_to_work_date || a.returnToWorkDate,
      coverageDetails: a.coverage_details || a.coverageDetails,
      reviewedByName: a.reviewed_by_name || a.reviewedByName,
      reviewedAt: a.reviewed_at || a.reviewedAt,
      ...parseLeaveApplicationAttributes(a)
    }
  })
}

const STATUS_SECTION_ORDER = ["pending", "approved", "declined"]

function groupByStatus(rows) {
  const buckets = new Map()
  for (const row of rows) {
    const status = row.status || "pending"
    if (!buckets.has(status)) buckets.set(status, [])
    buckets.get(status).push(row)
  }
  return STATUS_SECTION_ORDER
    .filter(status => buckets.has(status))
    .map(status => ({ status, rows: buckets.get(status) }))
}

function statusBadgeVariant(status) {
  if (status === "approved") return "success"
  if (status === "declined") return "danger"
  return "warning"
}

function apiErrorMessage(error, fallback) {
  const errs = error?.response?.data?.errors
  if (Array.isArray(errs)) {
    return errs.map(er => (typeof er === "string" ? er : er.detail || er.title)).filter(Boolean).join(" ")
  }
  return error?.message || fallback
}

export default function LeaveApplicationsDashboard() {
  const baseUrl = useSelector(getBaseUrl)
  const currentUser = useSelector(getCurrentUser)
  const currentRole = useSelector(getCurrentRole)
  const systemRole = currentUser?.attributes?.role
  const admin = isSystemAdmin(systemRole)
  const operationsTeam = roleCheck(systemRole, currentRole, OPERATIONS)
  const accountsOnlyView = isAccountsTeam(currentRole) && !admin && !operationsTeam
  const canReview = admin
  const canManageCoverage = canEditCoverage(systemRole, currentRole)

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitterOptions, setSubmitterOptions] = useState([])
  const [filterUserId, setFilterUserId] = useState("")
  const [filterDateFrom, setFilterDateFrom] = useState("")
  const [filterDateTo, setFilterDateTo] = useState("")
  const [filterStatus, setFilterStatus] = useState(() => (admin || operationsTeam ? "pending" : "approved"))
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [approvingId, setApprovingId] = useState(null)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [showCoverageModal, setShowCoverageModal] = useState(false)
  const [coverageRowId, setCoverageRowId] = useState(null)
  const [coverageDetails, setCoverageDetails] = useState("")
  const [savingCoverage, setSavingCoverage] = useState(false)
  const [declineRowId, setDeclineRowId] = useState(null)
  const [declineReason, setDeclineReason] = useState("")
  const [declining, setDeclining] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  const defaultWeek = useMemo(() => currentWeekPeriod(), [])
  const [printPeriodFrom, setPrintPeriodFrom] = useState(defaultWeek.from)
  const [printPeriodTo, setPrintPeriodTo] = useState(defaultWeek.to)
  const [printStatusFilter, setPrintStatusFilter] = useState("all")
  const [printingPdf, setPrintingPdf] = useState(false)
  const [printReportOpen, setPrintReportOpen] = useState(false)

  const loadRows = useCallback(() => {
    if (!baseUrl) return Promise.resolve()
    setLoading(true)
    const queryParams = {}
    if (filterUserId) queryParams.user_id = Number(filterUserId)
    if (filterDateFrom) queryParams.last_day_of_work_from = filterDateFrom
    if (filterDateTo) queryParams.last_day_of_work_to = filterDateTo
    if (!accountsOnlyView && filterStatus && filterStatus !== "all") {
      queryParams.status_filter = filterStatus
    }

    return getLeaveApplicationsDashboard(baseUrl, queryParams)
      .then(res => {
        if (res.status === 200 && res.response) {
          setRows(parseDashboardRows(res.response))
          const submitters = res.response.meta?.submitters
          if (Array.isArray(submitters)) setSubmitterOptions(submitters)
        } else if (res.error) {
          toast.error("Could not load leave applications.")
        }
      })
      .finally(() => setLoading(false))
  }, [baseUrl, filterUserId, filterDateFrom, filterDateTo, filterStatus, accountsOnlyView])

  useEffect(() => {
    if (!canAccessDashboard(systemRole, currentRole)) {
      window.location = "/"
      return
    }
    loadRows()
  }, [loadRows, systemRole, currentRole])

  const sections = useMemo(() => groupByStatus(rows), [rows])

  const hasActiveFilters = Boolean(
    filterUserId
    || filterDateFrom
    || filterDateTo
    || (!accountsOnlyView && filterStatus && filterStatus !== "all")
  )

  const handleApprove = async row => {
    if (!window.confirm(`Approve leave application for ${row.submitterName || row.firstname}?`)) return
    setApprovingId(row.id)
    const res = await approveLeaveApplication(baseUrl, row.id)
    setApprovingId(null)
    if (res.error) {
      toast.error(apiErrorMessage(res.error, "Could not approve application."))
      return
    }
    toast.success("Leave application approved. A leave record was added for this user.")
    loadRows()
  }

  const openDecline = row => {
    setDeclineRowId(row.id)
    setDeclineReason("")
    setShowDeclineModal(true)
  }

  const openCoverage = row => {
    setCoverageRowId(row.id)
    setCoverageDetails(row.coverageDetails || "")
    setShowCoverageModal(true)
  }

  const handleSaveCoverage = async () => {
    if (!coverageDetails.trim()) {
      toast.error("Please enter leave cover and assigned duties.")
      return
    }
    setSavingCoverage(true)
    const res = await updateLeaveApplicationCoverage(baseUrl, coverageRowId, coverageDetails.trim())
    setSavingCoverage(false)
    if (res.error) {
      toast.error(apiErrorMessage(res.error, "Could not save coverage details."))
      return
    }
    toast.success("Coverage details saved.")
    setShowCoverageModal(false)
    setCoverageRowId(null)
    loadRows()
  }

  const handlePrintReport = async () => {
    if (!printPeriodFrom || !printPeriodTo) {
      toast.error("Please select a print period.")
      return
    }
    if (printPeriodTo < printPeriodFrom) {
      toast.error("Period end must be on or after period start.")
      return
    }

    setPrintingPdf(true)
    const queryParams = {
      period_from: printPeriodFrom,
      period_to: printPeriodTo
    }
    if (printStatusFilter && printStatusFilter !== "all") {
      queryParams.status_filter = printStatusFilter
    }
    if (filterUserId) {
      queryParams.user_id = Number(filterUserId)
    }

    const res = await openLeaveApplicationsDashboardPdf(baseUrl, queryParams)
    setPrintingPdf(false)

    if (res.error) {
      toast.error(res.error)
    }
  }

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      toast.error("Please enter a decline reason.")
      return
    }
    setDeclining(true)
    const res = await declineLeaveApplication(baseUrl, declineRowId, declineReason.trim())
    setDeclining(false)
    if (res.error) {
      toast.error(apiErrorMessage(res.error, "Could not decline application."))
      return
    }
    toast.success("Leave application declined.")
    setShowDeclineModal(false)
    setDeclineRowId(null)
    loadRows()
  }

  if (!canAccessDashboard(systemRole, currentRole)) return null

  return (
    <Container fluid>
      <div className="mb-3">
        <Link to="/settings">← Settings</Link>
      </div>
      <h1 className="mb-4">{accountsOnlyView ? "Approved Leave" : "Leave Applications"}</h1>
      <p className="text-muted mb-4">
        {accountsOnlyView
          ? "View approved surveyor leave requests."
          : operationsTeam && !admin
            ? "Review surveyor leave requests and record leave cover and assigned duties."
            : "Review surveyor leave requests. Approving creates a leave entry on the user\u2019s profile and operation schedule."}
      </p>

      {canReview && (
        <>
          <div className="mb-3 d-flex flex-wrap align-items-center">
            <Button
              type="button"
              variant="link"
              className="p-0"
              onClick={() => setPrintReportOpen(o => !o)}
              aria-expanded={printReportOpen}>
              {printReportOpen ? "- Hide print report" : "+ Print leave request report"}
            </Button>
          </div>
          <Collapse in={printReportOpen}>
            <div className="mb-4 p-3 border rounded bg-light">
              <p className="text-muted small mb-3">
                Defaults to the current week (Wednesday through Tuesday).
                Includes leave that overlaps the selected dates.
              </p>
              <Row>
                <Col md={6} lg={3} className="mb-3">
                  <Form.Group className="mb-0">
                    <Form.Label>Period from</Form.Label>
                    <Form.Control
                      type="date"
                      value={printPeriodFrom}
                      onChange={e => setPrintPeriodFrom(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} lg={3} className="mb-3">
                  <Form.Group className="mb-0">
                    <Form.Label>Period to</Form.Label>
                    <Form.Control
                      type="date"
                      value={printPeriodTo}
                      onChange={e => setPrintPeriodTo(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} lg={3} className="mb-3">
                  <Form.Group className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={printStatusFilter}
                      onChange={e => setPrintStatusFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={6} lg={3} className="mb-3 d-flex align-items-end">
                  <Button
                    type="button"
                    variant="primary"
                    disabled={printingPdf}
                    onClick={handlePrintReport}>
                    {printingPdf ? "Opening PDF…" : "Print report (PDF)"}
                  </Button>
                </Col>
              </Row>
              <Button
                type="button"
                variant="link"
                className="p-0 small"
                onClick={() => {
                  const period = currentWeekPeriod()
                  setPrintPeriodFrom(period.from)
                  setPrintPeriodTo(period.to)
                }}>
                Reset to current week
              </Button>
            </div>
          </Collapse>
        </>
      )}

      <div className="mb-3 d-flex flex-wrap align-items-center">
        <Button
          type="button"
          variant="link"
          className="p-0"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}>
          {filtersOpen ? "- Hide filters" : "+ Show filters"}
        </Button>
        {hasActiveFilters && !filtersOpen && (
          <span className="text-muted small ml-2">Filters active</span>
        )}
      </div>

      <Collapse in={filtersOpen}>
        <div className="mb-4">
          <Form>
            <Row>
              <Col md={6} lg={3} className="mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Surveyor</Form.Label>
                  <Form.Control as="select" value={filterUserId} onChange={e => setFilterUserId(e.target.value)}>
                    <option value="">All surveyors</option>
                    {submitterOptions.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6} lg={3} className="mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Last day of work from</Form.Label>
                  <Form.Control type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6} lg={3} className="mb-3">
                <Form.Group className="mb-0">
                  <Form.Label>Last day of work to</Form.Label>
                  <Form.Control type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
                </Form.Group>
              </Col>
              {!accountsOnlyView && (
                <Col md={6} lg={3} className="mb-3">
                  <Form.Group className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <Form.Control as="select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setFilterUserId("")
                setFilterDateFrom("")
                setFilterDateTo("")
                setFilterStatus(admin || operationsTeam ? "pending" : "approved")
              }}
              disabled={!hasActiveFilters}>
              Clear filters
            </Button>
          </Form>
        </div>
      </Collapse>

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-muted">
          {hasActiveFilters ? "No applications match these filters." : "No leave applications yet."}
        </p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Status</th>
              <th>Surveyor</th>
              <th>Leave type</th>
              <th>Start</th>
              <th>End</th>
              <th>Days off</th>
              <th style={{ minWidth: "200px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sections.map(section => (
              <React.Fragment key={section.status}>
                <tr className="table-light">
                  <td colSpan={7}>
                    <strong>{section.status.charAt(0).toUpperCase() + section.status.slice(1)}</strong>
                    <span className="text-muted ml-2">
                      (
                      {section.rows.length}
                      )
                    </span>
                  </td>
                </tr>
                {section.rows.map(row => {
                  const pending = row.status === "pending"
                  const expanded = expandedId === row.id
                  const removedFromProfile = row.blockOutRemoved
                  return (
                    <React.Fragment key={row.id}>
                      <tr>
                        <td>
                          <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
                          {removedFromProfile && (
                            <Badge variant="secondary" className="ml-1">Removed from profile</Badge>
                          )}
                        </td>
                        <td {...removedFromProfileCellProps(removedFromProfile)}>
                          <div>{row.submitterName || `${row.firstname} ${row.surname}`}</div>
                          <div className="text-muted small">{row.submitterEmail}</div>
                        </td>
                        <td {...removedFromProfileCellProps(removedFromProfile)}>{row.leaveTypeLabel}</td>
                        <td {...removedFromProfileCellProps(removedFromProfile)}>
                          {row.periodStartLabel || row.lastDayOfWork}
                        </td>
                        <td {...removedFromProfileCellProps(removedFromProfile)}>
                          {row.periodEndLabel || row.returnToWorkDate}
                        </td>
                        <td {...removedFromProfileCellProps(removedFromProfile)}>
                          {row.daysOffLabel ?? row.workingDaysOff}
                        </td>
                        <td>
                          <div className="d-flex flex-wrap" style={{ gap: "0.35rem" }}>
                            <Button
                              type="button"
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setExpandedId(expanded ? null : row.id)}>
                              {expanded ? "Hide" : "Details"}
                            </Button>
                            {pending && canReview && (
                              <>
                                <Button
                                  type="button"
                                  variant="success"
                                  size="sm"
                                  disabled={approvingId === row.id}
                                  onClick={() => handleApprove(row)}>
                                  {approvingId === row.id ? "Approving…" : "Approve"}
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => openDecline(row)}>
                                  Decline
                                </Button>
                              </>
                            )}
                            {canManageCoverage && (
                              <Button
                                type="button"
                                variant="outline-primary"
                                size="sm"
                                onClick={() => openCoverage(row)}>
                                {row.coverageDetails ? "Edit coverage" : "Add coverage"}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={7} className="bg-light">
                            <div className="small">
                              <p className="mb-1">
                                <strong>Position:</strong>
                                {" "}
                                {row.position}
                                {" "}
                                |
                                <strong> Phone:</strong>
                                {" "}
                                {row.phone}
                              </p>
                              {row.comments && (
                                <p className="mb-1">
                                  <strong>Comments:</strong>
                                  {" "}
                                  {row.comments}
                                </p>
                              )}
                              <p className="mb-1">
                                <strong>Leave cover and assigned duties:</strong>
                                {" "}
                                {row.coverageDetails || "—"}
                                {canManageCoverage && (
                                  <Button
                                    type="button"
                                    variant="link"
                                    className="p-0 ml-2 align-baseline"
                                    onClick={() => openCoverage(row)}>
                                    {row.coverageDetails ? "Edit" : "Add"}
                                  </Button>
                                )}
                              </p>
                              {row.status === "declined" && (
                                <p className="mb-1 text-danger">
                                  <strong>Decline reason:</strong>
                                  {" "}
                                  {row.declinedReason || "—"}
                                </p>
                              )}
                              {row.reviewedByName && (
                                <p className="mb-0 text-muted">
                                  Reviewed by
                                  {" "}
                                  {row.reviewedByName}
                                  {row.reviewedAt ? ` on ${new Date(row.reviewedAt).toLocaleString()}` : ""}
                                </p>
                              )}
                              {removedFromProfile && (
                                <p className="mb-0 text-muted">
                                  <strong>Profile leave removed:</strong>
                                  {" "}
                                  {row.blockOutRemovedAt
                                    ? new Date(row.blockOutRemovedAt).toLocaleString()
                                    : "Yes"}
                                </p>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showDeclineModal} onHide={() => setShowDeclineModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Decline leave application</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reason for refusal</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeclineModal(false)} disabled={declining}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDecline} disabled={declining}>
            {declining ? "Declining…" : "Decline"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCoverageModal} onHide={() => setShowCoverageModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Leave cover and assigned duties</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              Who will cover this role while the surveyor is on leave and what duties will they perform?
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={coverageDetails}
              onChange={e => setCoverageDetails(e.target.value)}
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCoverageModal(false)} disabled={savingCoverage}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCoverage} disabled={savingCoverage}>
            {savingCoverage ? "Saving…" : "Save coverage"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
